import { useMemo } from 'react'

import { useMePermissionByAccountsAndPermissionsApi } from 'api/alphaZulu/queries/useMePermissionByAccountsAndPermissionsApi'
import { useHierarchyTreeApi } from 'api/navigation/queries/useHierarchyTreeApi'
import { HierarchyDefaults } from 'constants/hierarchy'
import { Permission } from 'constants/permission'
import { useWorkspaceAccountIds } from 'hooks/useWorkspaceAccountIds'
import { useCurrentTenantData } from 'providers/currentTenantData/CurrentTenantDataContext'
import { HierarchyTree } from 'types/hierarchy/hierarchy'
import { transformObject } from 'utils/common'
import { isHierarchyNode } from 'utils/navigation'

export const useUserCanManageHierarchy = (isLayerAdminPage = false) => {
  const {
    currentTenant: { id },
  } = useCurrentTenantData()

  const { isLoading, data: hierarchyTree } = useHierarchyTreeApi({
    params: {
      tenantId: id,
    },
    staleTime: HierarchyDefaults.StaleTime,
  })
  const hierarchyTreeWithAliases = useMemo(
    () =>
      hierarchyTree
        ? {
            ...hierarchyTree,
            mapping: transformObject(hierarchyTree?.mapping!, node => ({
              ...node,
              name: isHierarchyNode(node) ? node.alias || node.name : node.name,
            })),
          }
        : null,
    [hierarchyTree],
  ) as HierarchyTree

  const accountIds = useWorkspaceAccountIds(hierarchyTree)
  const {
    data: hierarchyManagePermissions,
    isLoading: isPermissionsLoading,
    isFetched,
  } = useMePermissionByAccountsAndPermissionsApi({
    params: {
      account_ids: accountIds,
      perms: [Permission.OS_WORKSPACE_MANAGE],
      perm_prefixes: [Permission.OS_PREFIX],
    },
    enabled: !!hierarchyTree && isLayerAdminPage,
  })

  return {
    isUserCanManage: !isFetched || !!hierarchyManagePermissions?.length,
    hierarchyManagePermissions,
    hierarchyTree: hierarchyTreeWithAliases,
    isLoading,
    isPermissionsLoading,
  }
}
